import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import ImageDropUpload from "../../components/ImageDropUpload";
import { useLandingConfig } from "../../hooks/useLandingConfig";
import { Save, Globe } from "lucide-react";

export default function WebInfoConfigPage() {
    const { config, setConfig, loading, saving, save } = useLandingConfig();

    if (loading) return <div className="text-gold-500">加载中...</div>;

    return (
        <div className="space-y-6" data-testid="web-info-config-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">落地页</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1 flex items-center gap-3">
                        <Globe className="h-7 w-7 text-gold-600" /> 网站信息
                    </h1>
                    <p className="text-sm text-gold-500 mt-1">
                        网页标题、SEO、页脚品牌、联系方式 —— 让搜索引擎和访客都找得到你
                    </p>
                </div>
                <Button
                    data-testid="webinfo-save-btn-top"
                    onClick={() => save()}
                    disabled={saving}
                    className="h-10 px-5 bg-gold-600 hover:bg-gold-700 text-white gap-2 shrink-0"
                >
                    <Save className="h-4 w-4" /> {saving ? "保存中..." : "保存"}
                </Button>
            </div>

            {/* Site title */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">网页标题</h2>
                <div className="space-y-1.5">
                    <Label>浏览器标签显示的标题</Label>
                    <Input
                        data-testid="lc-site-title"
                        value={config.site_title}
                        onChange={(e) => setConfig({ ...config, site_title: e.target.value })}
                        placeholder="福缘珠宝 Fuyuan Jewelry — 臻品现货发售"
                    />
                    <div className="text-xs text-gold-500">落地页打开时自动读取，显示在浏览器标签页与分享预览。</div>
                </div>
            </Card>

            <Card className="p-6 border-slate-200 shadow-sm space-y-4" data-testid="lc-logo-card">
                <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">网站 Logo</h2>
                    <p className="text-xs text-slate-500 mt-1">
                        保存后落地页会自动读取新 Logo；留空则继续使用目前的默认福缘 Logo。
                    </p>
                </div>
                <div className="space-y-2">
                    <Label>导航栏 Logo 图片</Label>
                    <ImageDropUpload
                        value={config.logo_url}
                        onChange={(value) => setConfig({ ...config, logo_url: value || "" })}
                        maxEdge={800}
                        previewClassName="aspect-[5/2] max-w-md"
                        testIdPrefix="landing-logo"
                        preserveTransparency
                        accept="image/png,image/jpeg,image/webp"
                        helperText="PNG / JPG / WebP · 自动压缩至 800px · PNG 会保留透明背景"
                    />
                </div>
                <div className="grid gap-3 text-xs text-slate-600 sm:grid-cols-2">
                    <div className="border-l-2 border-gold-400 pl-3">
                        <div className="font-semibold text-slate-800">推荐格式</div>
                        <p className="mt-1">PNG 透明背景最好看，适合米白色／毛玻璃导航栏。</p>
                    </div>
                    <div className="border-l-2 border-gold-400 pl-3">
                        <div className="font-semibold text-slate-800">建议尺寸</div>
                        <p className="mt-1">横向 Logo 原图高度至少 240px，可清晰显示在 44–48px 导航栏。</p>
                    </div>
                    <div className="border-l-2 border-gold-400 pl-3">
                        <div className="font-semibold text-slate-800">图片比例</div>
                        <p className="mt-1">横向或方形都可；建议宽度不超过 500px，避免手机上显示太小。</p>
                    </div>
                    <div className="border-l-2 border-gold-400 pl-3">
                        <div className="font-semibold text-slate-800">文件大小</div>
                        <p className="mt-1">建议控制在 200KB 内，帮助落地页首屏保持快速加载。</p>
                    </div>
                </div>
            </Card>

            {/* Footer brand */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4" data-testid="lc-footer-brand-card">
                <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">页脚品牌</h2>
                    <p className="text-xs text-slate-500 mt-1">落地页最底部的品牌与版权文字。</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label>品牌大号中文</Label>
                        <Input
                            data-testid="lc-footer-brand-title"
                            value={config.footer_brand_title}
                            onChange={(e) => setConfig({ ...config, footer_brand_title: e.target.value })}
                            placeholder="福缘珠宝"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>品牌下英文小字</Label>
                        <Input
                            data-testid="lc-footer-brand-sub"
                            value={config.footer_brand_sub}
                            onChange={(e) => setConfig({ ...config, footer_brand_sub: e.target.value })}
                            placeholder="Fuyuan Jewelry — since 1989"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label>版权行（左）</Label>
                        <Input
                            data-testid="lc-footer-copyright"
                            value={config.footer_copyright}
                            onChange={(e) => setConfig({ ...config, footer_copyright: e.target.value })}
                            placeholder="© 2026 福缘珠宝 FUYUAN JEWELRY. 版权所有"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>标语（右）</Label>
                        <Input
                            data-testid="lc-footer-tagline"
                            value={config.footer_tagline}
                            onChange={(e) => setConfig({ ...config, footer_tagline: e.target.value })}
                            placeholder="Crafted with care in Malaysia"
                        />
                    </div>
                </div>
            </Card>

            {/* Footer contact */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4" data-testid="lc-footer-card">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">页脚联系方式</h2>
                <div className="space-y-1.5">
                    <Label>联系电话</Label>
                    <Input data-testid="lc-footer-phone" value={config.footer_phone} onChange={(e) => setConfig({ ...config, footer_phone: e.target.value })} placeholder="+6012-345 6789（WhatsApp）" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label>地址（多行）</Label>
                        <Textarea rows={3} data-testid="lc-footer-address" value={config.footer_address} onChange={(e) => setConfig({ ...config, footer_address: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                        <Label>营业时间（多行）</Label>
                        <Textarea rows={3} data-testid="lc-footer-hours" value={config.footer_hours} onChange={(e) => setConfig({ ...config, footer_hours: e.target.value })} />
                    </div>
                </div>
            </Card>

            <Card className="p-6 border-slate-200 shadow-sm space-y-5" data-testid="lc-social-links-card">
                <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">
                        社媒与购物平台链接
                    </h2>
                    <p className="mt-1 text-xs text-slate-500">
                        全部为可选项。留空时落地页会自动隐藏对应图标或文字链接。
                    </p>
                </div>
                <div className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-700">社交媒体</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <LinkField
                            label="Facebook 链接"
                            testId="lc-social-facebook"
                            value={config.social_facebook}
                            onChange={(value) => setConfig({ ...config, social_facebook: value })}
                            placeholder="https://www.facebook.com/fuyuanjewelry"
                        />
                        <LinkField
                            label="Instagram 链接"
                            testId="lc-social-instagram"
                            value={config.social_instagram}
                            onChange={(value) => setConfig({ ...config, social_instagram: value })}
                            placeholder="https://www.instagram.com/fuyuanjewelry"
                        />
                        <LinkField
                            label="TikTok 链接"
                            testId="lc-social-tiktok"
                            value={config.social_tiktok}
                            onChange={(value) => setConfig({ ...config, social_tiktok: value })}
                            placeholder="https://www.tiktok.com/@fuyuanjewelry"
                        />
                    </div>
                </div>
                <div className="space-y-3 border-t border-slate-200 pt-5">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-700">购物平台</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <LinkField
                            label="Shopee 店铺链接"
                            testId="lc-shop-shopee"
                            value={config.shop_shopee}
                            onChange={(value) => setConfig({ ...config, shop_shopee: value })}
                            placeholder="https://shopee.com.my/fuyuanjewelry88"
                        />
                        <LinkField
                            label="Lazada 店铺链接"
                            testId="lc-shop-lazada"
                            value={config.shop_lazada}
                            onChange={(value) => setConfig({ ...config, shop_lazada: value })}
                            placeholder="https://www.lazada.com.my/shop/fuyuanjewelry"
                        />
                    </div>
                </div>
                <p className="text-xs text-gold-600">
                    建议使用完整链接（以 https:// 开头）；系统不会强制验证，保存后请在落地页点击确认。
                </p>
            </Card>

            {/* SEO */}
            <Card className="p-6 border-slate-200 shadow-sm space-y-4" data-testid="lc-seo-card">
                <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">SEO</h2>
                    <p className="text-xs text-slate-500 mt-1">用于网页 meta description、Google 搜索片段、社群预览与页脚「专营」行。</p>
                </div>
                <div className="space-y-1.5">
                    <Label>SEO 描述（建议 60–160 字）</Label>
                    <Textarea
                        rows={3}
                        data-testid="lc-seo-description"
                        value={config.seo_description}
                        onChange={(e) => setConfig({ ...config, seo_description: e.target.value })}
                        placeholder="福缘珠宝 Fuyuan Jewelry — 老天珠、水晶、翡翠..."
                    />
                    <div className="text-xs text-gold-500">
                        {(config.seo_description || "").length} / 160 字
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label>关键词（每行一个）</Label>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid="lc-seo-add-keyword"
                            onClick={() => setConfig({ ...config, seo_keywords: [...(config.seo_keywords || []), ""] })}
                            className="h-7 text-xs gap-1"
                        >
                            + 加一个
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {(config.seo_keywords || []).map((kw, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <Input
                                    data-testid={`lc-seo-keyword-${idx}`}
                                    value={kw}
                                    onChange={(e) => setConfig({
                                        ...config,
                                        seo_keywords: (config.seo_keywords || []).map((x, i) => i === idx ? e.target.value : x),
                                    })}
                                    placeholder="例：老天珠"
                                    className="flex-1"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setConfig({
                                        ...config,
                                        seo_keywords: (config.seo_keywords || []).filter((_, i) => i !== idx),
                                    })}
                                    className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="删除此关键词"
                                >
                                    ×
                                </Button>
                            </div>
                        ))}
                        {(!config.seo_keywords || config.seo_keywords.length === 0) && (
                            <div className="text-xs text-slate-400 italic">暂无关键词，点右上「加一个」开始添加</div>
                        )}
                    </div>
                    <div className="text-xs text-gold-500">建议 5–10 个精准词，落地页页脚会显示为「专营：xx · xx · xx」；最多 24 个。</div>
                </div>

                <div className="pt-4 border-t border-slate-200 space-y-1.5">
                    <Label>Google Search Console 验证码</Label>
                    <Input
                        data-testid="lc-google-site-verification"
                        value={config.google_site_verification}
                        onChange={(e) => setConfig({ ...config, google_site_verification: e.target.value })}
                        placeholder='粘贴 Google 给的整段 <meta name="google-site-verification" ...> 或只贴 content 值'
                        className="font-mono text-xs"
                    />
                    <div className="text-xs text-gold-500">
                        落地页会自动写成 <code className="bg-white/80 px-1 rounded border border-gold-200">&lt;meta name="google-site-verification" content="..."&gt;</code>。用来在 Google Search Console 认证网站归属，认证后才能提交 sitemap 与查看搜索表现。
                    </div>
                </div>
            </Card>

            {/* Floating save button */}
            <div className="fixed bottom-6 right-6 z-40">
                <Button data-testid="webinfo-save-btn" onClick={() => save()} disabled={saving}
                    className="h-12 px-6 bg-gold-600 hover:bg-gold-700 text-white gap-2 shadow-xl shadow-gold-900/20 rounded-full">
                    <Save className="h-4 w-4" /> {saving ? "保存中..." : "保存网站信息"}
                </Button>
            </div>
        </div>
    );
}

function LinkField({ label, testId, value, onChange, placeholder }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={testId}>{label}</Label>
            <Input
                id={testId}
                type="url"
                data-testid={testId}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
            />
        </div>
    );
}
